import React from 'react';
import { withRouter } from 'react-router-dom';

// material components
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import StyledBadge from '@material-ui/core/Badge';
import IconButton from '@material-ui/core/IconButton';

// custom components
import AlertsDrawer from './AlertsDrawer';
import withShipment from '../../withShipment';

// constants
import { SHOW_NO_CLEAR_IMAGE_TAB, canSeeAccessRequests } from './constants';

// redux
import { ALERTS_CHANGED_EVENT } from '../../redux/constants/alerts';
import {
  alertEntriesSelector,
  alertSummarySelector,
} from '../../redux/selectors/alerts';
import { userInfoSelector } from '../../redux/selectors/auth';
import { refreshUserInfo } from '../../redux/actions/auth';
import {
  fetchAlerts,
  fetchAlertSummary,
  resolveAlert,
  deleteAllAlerts,
} from '../../redux/actions/alerts';
import PropTypes from 'prop-types';

import { tableSettingSelector } from '../../redux/selectors/account';
import { getTableSetting } from '../../redux/actions/account';
import { Tooltip } from '@mui/material';

// Cross-company access requests share the bell: the alert row is the
// nudge, the Access Requests tab is where they are acted on.
import {
  fetchAccessRequests,
  decideAccessRequest,
} from '../../redux/actions/accessRequests';
import {
  incomingAccessRequestsSelector,
  incomingDecidedAccessSelector,
  outgoingAccessRequestsSelector,
  accessRequestBadgeCountSelector,
} from '../../redux/selectors/accessRequests';

// styling

class Alerts extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      alertDrawerOpen: false,
      alerts: [],
    };
  }

  handleAlertDrawerClose = () => {
    this.setState({
      alertDrawerOpen: false,
    });
  };

  parseAlertTimestamp = (ts) => {
    if (!ts) return 0;
    // Format: "MM\dd\yyyy h:mm a" or "MM/dd/yyyy h:mm a"
    const parts = ts.trim().split(/\s+/);
    const datePart = parts[0];
    const timePart = parts[1] || '00:00';
    const meridiem = (parts[2] || 'AM').toUpperCase();

    const [mm, dd, yyyy] = datePart.split(/[/\\]/);
    let [h, min] = timePart.split(':').map(Number);
    if (meridiem === 'PM' && h < 12) h += 12;
    if (meridiem === 'AM' && h === 12) h = 0;

    return new Date(
      parseInt(yyyy),
      parseInt(mm) - 1,
      parseInt(dd),
      h,
      min
    ).getTime();
  };

  // Parse once per alert, not twice per comparison. The comparator used
  // to call parseAlertTimestamp on both sides of every comparison, so a
  // list of n cost ~2n*log(n) date constructions on the main thread every
  // poll tick. Decorate-sort-undecorate makes it n.
  sortAlertsByLatest = (alerts) => {
    return [...alerts]
      .map((alert) => ({
        alert,
        at: this.parseAlertTimestamp(alert.timestamp),
      }))
      .sort((a, b) => b.at - a.at)
      .map((entry) => entry.alert);
  };

  // Reconnect safety net, NOT the delivery path.
  //
  // AlertService now pings 'refreshAlerts' over the socket on every alert
  // create, edit and delete, so the bell updates the moment something
  // happens. This interval exists only to cover a dropped message or a
  // socket that reconnected while an alert was raised — cases the push
  // cannot cover by definition.
  //
  // It was 30s and was the ONLY way most alert types ever reached the
  // browser: every open tab fetched 300 full rows twice a minute, forever,
  // to render a badge that almost never changed.
  ALERT_POLL_MS = 300000;

  componentDidMount() {
    this.props.getTableSetting({});

    // Counts for the badge, which is on screen from the moment the header
    // renders. The rows behind them are pulled only when the drawer opens.
    this.refreshSummary();

    // One full read at mount so the drawer has something to show the first
    // time it is opened, instead of an empty list for the length of a
    // round trip. Steady state after this is summary-only.
    this.refreshAlerts();

    this.setState({ alerts: this.sortAlertsByLatest(this.props.alertEntries) });

    this.alertPoll = setInterval(this.refreshSummary, this.ALERT_POLL_MS);

    // Base.jsx owns the socket and hands every server 'refreshAlerts' push
    // over as this event. It deliberately does not fetch anything itself:
    // what to re-read depends on the drawer being open and on the
    // owner-tier guard below, both of which live here.
    window.addEventListener(ALERTS_CHANGED_EVENT, this.handleAlertsChanged);
  }

  componentWillUnmount() {
    if (this.alertPoll) clearInterval(this.alertPoll);
    clearTimeout(this._pushDebounce);
    window.removeEventListener(ALERTS_CHANGED_EVENT, this.handleAlertsChanged);
  }

  // A push from the server. Always re-count; only re-read the rows if the
  // drawer is actually open and looking at them.
  //
  // Debounced because a single change can legitimately arrive twice: the
  // server pushes over both the general and the RFID socket (owners and
  // admins only hold the latter), and a tech admin holds both. It also
  // collapses bursts — a job raising several alerts at once fires one
  // ping each, and the bell only needs to re-count after the last.
  PUSH_DEBOUNCE_MS = 400;

  handleAlertsChanged = () => {
    clearTimeout(this._pushDebounce);
    this._pushDebounce = setTimeout(() => {
      // refreshSummary already pulls the access-request queue alongside
      // the counts.
      this.refreshSummary();
      if (this.state.alertDrawerOpen) this.refreshAlerts();
    }, this.PUSH_DEBOUNCE_MS);
  };

  // An access decision made by ANOTHER company's owner changes this
  // user's own location list server-side. Their session cannot know
  // that on its own, so the decision alert — which lands at their home
  // company and is already being polled for — is used as the signal to
  // re-read the account. Without it a granted location stays invisible
  // until the next login: the access is real, the picker just never
  // hears about it.
  ACCESS_DECISION_TYPE = 'ACCESS_REQUEST_DECISION';

  refreshOnAccessDecision = (summary) => {
    const latestId = summary?.latestDecisionAlertId;
    if (!latestId || latestId === this.lastSeenDecisionAlertId) return;
    this.lastSeenDecisionAlertId = latestId;
    this.props.refreshUserInfo();
  };

  // Owner tier ONLY. /accessRequest/* is @Secured to the owner tier and
  // answers everyone else with 403 - which AuthUtil reads as a dead
  // session and logs out. An employee or supervisor was therefore being
  // signed out every poll tick. Their queue is empty by definition, so
  // there is nothing to fetch: don't ask.
  refreshAccessRequests = () => {
    const locationId = this.props.userInfo?.selectedLocationId;
    if (!locationId) return;
    if (!canSeeAccessRequests(this.props.userInfo)) return;
    this.props.fetchAccessRequests({
      locationId,
      companyId: this.props.userInfo?.companyId,
    });
  };

  // Counts only — what the badge actually renders.
  refreshSummary = () => {
    const locationId = this.props.userInfo?.selectedLocationId;
    if (!locationId) return;
    this.props.fetchAlertSummary({ locationId });
    this.refreshAccessRequests();
  };

  // The rows. Only worth pulling when something is going to display them.
  refreshAlerts = () => {
    const locationId = this.props.userInfo?.selectedLocationId;
    if (!locationId) return;
    this.props.fetchAlerts({ locationId });
  };

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.alertEntries !== this.props.alertEntries) {
      this.sortAlertsIntoState();
    }
    // The decision signal now rides the summary, which arrives on every
    // push and tick — the rows only arrive when the drawer is opened, so
    // watching them would mean a granted location stayed invisible until
    // someone happened to click the bell.
    if (
      prevProps.alertSummary?.latestDecisionAlertId !==
      this.props.alertSummary?.latestDecisionAlertId
    ) {
      this.refreshOnAccessDecision(this.props.alertSummary);
    }
  }

  // Newest first for the drawer. Everything the server sends is already
  // filtered for this caller, so there is nothing to drop here.
  sortAlertsIntoState = () => {
    this.setState({
      alerts: this.sortAlertsByLatest(this.props.alertEntries || []),
    });
  };

  // While the "No Clear Image" tab is hidden the badge must count global alerts
  // only — otherwise it advertises entries the user can't reach anywhere. When
  // the flag is flipped back on, both kinds are reachable so both are counted.
  getBadgeCount = () => {
    // Straight from the count endpoint. It has already dropped the rows
    // exactly the rows the drawer will show, so the two always agree.
    const summary = this.props.alertSummary || {};
    const total = summary.count || 0;
    const alertCount = SHOW_NO_CLEAR_IMAGE_TAB
      ? total
      : Math.max(0, total - (summary.noClearSubjectCount || 0));
    // Pending access requests are counted too — they are the one thing in
    // this drawer that BLOCKS someone else's work until it is answered.
    const accessCount = canSeeAccessRequests(this.props.userInfo)
      ? this.props.accessRequestCount || 0
      : 0;
    return alertCount + accessCount;
  };

  toggleAlertDrawer = () => {
    // Opening is the moment the rows are needed. Everything up to here ran
    // on counts alone.
    this.refreshAlerts();
    this.setState({
      alertDrawerOpen: true,
    });
  };

  // "No clear subject" alert clicked — close the drawer and jump to Points
  // Watching (By session) for that date, with the giving employee filtered
  // and the "Unclear photos" chip pre-selected so the flagged entry is in view.
  navigateToPointWatching = (identifier) => {
    if (!identifier || !identifier.date) return;
    this.handleAlertDrawerClose();
    this.props.history.push('/point-watching', {
      pwDate: identifier.date,
      pwUserId: identifier.userId,
      pwUnclear: true,
    });
  };
  handleResolveAlert = (alertId, index) => {
    let data = {
      alertId: alertId,
      shippingTemplateChangedIndex: index,
      locationId: this.props.userInfo.selectedLocationId,
    };
    this.props.resolveAlert(data);
  };

  render() {
    return (
      <>
        <Tooltip title="Notifications">
          <IconButton
            style={{ marginRight: '4px' }}
            aria-label="alerts"
            onClick={this.toggleAlertDrawer}
          >
            <StyledBadge
              badgeContent={this.getBadgeCount()}
              color="error"
              overlap="rectangular"
            >
              <NotificationsActiveIcon
                color="secondary"
                style={{ fontSize: '30px' }}
              />
            </StyledBadge>
          </IconButton>
        </Tooltip>

        <AlertsDrawer
          alertDrawerOpen={this.state.alertDrawerOpen}
          handleAlertDrawerClose={this.handleAlertDrawerClose}
          alertEntries={this.state.alerts}
          noAlerts={this.state.alerts.length === 0}
          closeDrawer={this.handleAlertDrawerClose}
          resolveAlert={this.handleResolveAlert}
          user={this.props.userInfo}
          deleteAllAlert={this.props.deleteAllAlerts}
          onAlertNavigate={this.navigateToPointWatching}
          incomingRequests={this.props.incomingRequests}
          incomingDecided={this.props.incomingDecided}
          outgoingRequests={this.props.outgoingRequests}
          decideAccessRequest={this.props.decideAccessRequest}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  alertEntries: alertEntriesSelector(state),
  alertSummary: alertSummarySelector(state),
  userInfo: userInfoSelector(state),
  account: tableSettingSelector(state),
  incomingRequests: incomingAccessRequestsSelector(state),
  incomingDecided: incomingDecidedAccessSelector(state),
  outgoingRequests: outgoingAccessRequestsSelector(state),
  accessRequestCount: accessRequestBadgeCountSelector(state),
});

const actionCreators = {
  fetchAlerts,
  fetchAlertSummary,
  resolveAlert,
  getTableSetting,
  deleteAllAlerts,
  fetchAccessRequests,
  decideAccessRequest,
  refreshUserInfo,
};

Alerts.propTypes = {
  alertEntries: PropTypes.array,
  alertSummary: PropTypes.object,
  userInfo: PropTypes.object,
  account: PropTypes.object,
};

Alerts.defaultProps = {
  alertEntries: [],
  alertSummary: {},
  userInfo: {},
  account: {},
};

export default withRouter(
  withShipment(
    {
      mapStateToProps,
      actionCreators,
    },
    Alerts
  )
);
